/**
 * Risk Trend Chart
 * 
 * WHY: Managers and analysts need to see the trajectory of risk over time.
 * This visualization shows finding discovery trends, helping teams understand
 * if their remediation efforts are outpacing new vulnerability discovery.
 * 
 * PHASE 7 UPDATE: Now uses real API data via api.softwareiq.stats.getTrends
 * TD-034 RESOLVED: Removed generateMockTrendData in favor of real backend data.
 * 
 * @see .docs/architecture/phase6_dashboard_assessment.md - TD-034 Resolution
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { api, type RiskTrendPoint } from '@/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface RiskTrendChartProps {
  missionId: string
  className?: string
  /** Number of days of trend data to display */
  days?: number
}

/**
 * Transform API response to chart-friendly format.
 * Ensures dates are formatted for display.
 */
function formatTrendData(data: RiskTrendPoint[]): RiskTrendPoint[] {
  return data.map((point) => ({
    ...point,
    date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }))
}

/**
 * Calculate trend direction based on recent data.
 */
function calculateTrend(data: RiskTrendPoint[]): 'up' | 'down' | 'stable' {
  if (data.length < 7) return 'stable'
  
  const recent = data.slice(-7)
  const older = data.slice(-14, -7)
  
  if (older.length === 0) return 'stable'
  
  const recentAvg = recent.reduce((sum, d) => sum + d.total, 0) / recent.length
  const olderAvg = older.reduce((sum, d) => sum + d.total, 0) / older.length
  
  if (olderAvg === 0) return recentAvg > 0 ? 'up' : 'stable'
  
  const change = ((recentAvg - olderAvg) / olderAvg) * 100
  
  if (change > 5) return 'up'
  if (change < -5) return 'down'
  return 'stable'
}

/**
 * Custom tooltip for the area chart.
 */
function CustomTooltip({ active, payload, label }: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload) return null

  return (
    <div className="rounded-lg border bg-background/95 backdrop-blur-sm p-3 shadow-lg">
      <p className="font-medium mb-2">{label}</p>
      <div className="space-y-1">
        {payload.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-4 text-sm">
            <span className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}
            </span>
            <span className="font-mono tabular-nums">{entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function RiskTrendChart({ missionId, className, days = 30 }: RiskTrendChartProps) {
  // Fetch real trend data from the stats API
  const { data: rawTrendData, isLoading, error } = useQuery({
    queryKey: ['softwareiq', 'stats', 'trends', missionId, days],
    queryFn: () => api.softwareiq.stats.getTrends(missionId, days),
    enabled: !!missionId,
    staleTime: 60_000, // 1 minute - trends don't need real-time updates
    retry: 2,
  })

  // Format and memoize trend data
  const trendData = useMemo(
    () => (rawTrendData ? formatTrendData(rawTrendData) : []),
    [rawTrendData]
  )
  
  const trend = useMemo(() => calculateTrend(trendData), [trendData])

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus
  const trendColor = trend === 'up' ? 'text-destructive' : trend === 'down' ? 'text-green-500' : 'text-muted-foreground'
  const trendLabel = trend === 'up' ? 'Increasing' : trend === 'down' ? 'Decreasing' : 'Stable'

  // Error state
  if (error) {
    return (
      <Card className={cn('border-destructive/50', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Error Loading Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Failed to load risk trend data'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Risk Trend</CardTitle>
            <CardDescription>Finding discovery over the last {days} days</CardDescription>
          </div>
          {trendData.length > 0 && (
            <div className={cn('flex items-center gap-1 text-sm', trendColor)}>
              <TrendIcon className="h-4 w-4" />
              <span>{trendLabel}</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[300px] w-full" />
        ) : trendData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No trend data available</p>
              <p className="text-xs mt-1">Run scans to generate risk data</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorCritical" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorHigh" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorMedium" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#eab308" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#eab308" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorLow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                className="text-muted-foreground"
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                className="text-muted-foreground"
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
              />
              <Area
                type="monotone"
                dataKey="critical"
                name="Critical"
                stackId="1"
                stroke="#ef4444"
                fill="url(#colorCritical)"
              />
              <Area
                type="monotone"
                dataKey="high"
                name="High"
                stackId="1"
                stroke="#f97316"
                fill="url(#colorHigh)"
              />
              <Area
                type="monotone"
                dataKey="medium"
                name="Medium"
                stackId="1"
                stroke="#eab308"
                fill="url(#colorMedium)"
              />
              <Area
                type="monotone"
                dataKey="low"
                name="Low"
                stackId="1"
                stroke="#3b82f6"
                fill="url(#colorLow)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
